import React, { useRef, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer
]);

const getPVCChartOptions = (theme, getThemeColor, shiftData, machineName) => {
  const shifts = Object.keys(shiftData); // Shifts (A, B, C) on X-axis
  const colors = {
    totalPVC: theme === 'dark' ? `hsl(0, 70%, 50%)` : `hsl(0, 50%, 60%)`, // Reddish
    e75Extruder: theme === 'dark' ? `hsl(200, 70%, 50%)` : `hsl(200, 50%, 60%)`, // Blueish
    e45Extruder: theme === 'dark' ? `hsl(120, 70%, 50%)` : `hsl(120, 50%, 60%)` // Greenish
  };

  // Prepare series
  const series = [
    {
      name: 'Total PVC',
      type: 'bar',
      stack: 'Total',
      data: shifts.map(shift => shiftData[shift]?.TOTAL_CONSUMED || 0),
      itemStyle: {
        color: colors.totalPVC // Color for Total PVC bar
      },
      label: {
        show: true,
        position: 'top',
        formatter: () => 'L75', // Display the machine name instead of numeric value
        fontSize: 10,
        color: getThemeColor('gray-800')
      },
      barWidth: '12px'
    },
    {
      name: 'E75 Extruder PVC',
      type: 'bar',
      stack: 'Components',
      data: shifts.map(shift => shiftData[shift]?.E75_EXTRUDER_PVC || 0),
      itemStyle: {
        color: colors.e75Extruder
      },
      label: {
        show: false // No labels for components
      },
      barWidth: '12px'
    },
    {
      name: 'E45 Extruder PVC',
      type: 'bar',
      stack: 'Components',
      data: shifts.map(shift => shiftData[shift]?.E45_EXTRUDER_PVC || 0),
      itemStyle: {
        color: colors.e45Extruder
      },
      label: {
        show: false // No labels for components
      },
      barWidth: '12px'
    }
  ];

  return {
    tooltip: {
      trigger: 'item',
      formatter: ({ seriesName, value, name }) =>
        `<strong>${name}</strong><br>${seriesName}: ${value.toFixed(2)} kg`
    },
    legend: {
      data: ['Total PVC', 'E75 Extruder PVC', 'E45 Extruder PVC'],
      textStyle: {
        color: getThemeColor('gray-800'),
        fontSize: 12
      },
      bottom: 0
    },
    xAxis: {
      type: 'category',
      data: shifts, // Shifts on X-axis
      axisLabel: {
        fontSize: 12,
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        formatter: '{value} kg',
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: { color: getThemeColor('gray-300') }
      },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('gray-300') }
      }
    },
    series,
    grid: {
      left: '10%',
      right: '10%',
      bottom: '20%',
      top: '10%',
      containLabel: true
    },
    animation: true
  };
};

const PVCConsumptionChart = ({ shiftData, machineName, title }) => {
  const chartRef = useRef(null);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getPVCChartOptions(
            theme,
            getThemeColor,
            shiftData,
            machineName
          )}
          style={{ height: '400px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold'
  }
};

export default memo(PVCConsumptionChart);
